import { EyeOff, Star } from "lucide-react"
import { cn } from "@/lib/utils"

export type StudioQualityValue = "star" | "hidden" | null

export const STUDIO_FRAME_MARK_KEYS = ["video_first", "video_last", "transition_left", "transition_right"] as const

export type StudioFrameMarkKey = (typeof STUDIO_FRAME_MARK_KEYS)[number]

export function studioQualityBorderClass(quality: StudioQualityValue): string {
  if (quality === "star") return "border-2 border-amber-400"
  if (quality === "hidden") return "border-ui-border-strong"
  return ""
}

/** Overlay + star corner; parent must be `relative overflow-hidden`. */
export function StudioQualityPreviewDecor({ quality }: { quality: StudioQualityValue }) {
  if (quality === "hidden") {
    return (
      <div className="pointer-events-none absolute inset-0 z-[1] dark:bg-black/50 light:bg-clay-3/45" aria-hidden />
    )
  }
  if (quality === "star") {
    return (
      <span className="pointer-events-none absolute top-0.5 right-0.5 z-[2]" aria-hidden>
        <Star className="h-3.5 w-3.5 fill-amber-400 text-ui-status-review" strokeWidth={1.25} />
      </span>
    )
  }
  return null
}

type ToggleRowProps = {
  value: StudioQualityValue
  disabled?: boolean
  dense?: boolean
  appearance?: "chip" | "plain"
  /** Кнопка «Не подходит». На превью студии её нет: отметка скрытия там больше не нужна. */
  includeHidden?: boolean
  onSet: (next: StudioQualityValue) => void
}

export function StudioQualityMarkToggleRow({
  value,
  disabled,
  dense,
  appearance = "chip",
  includeHidden = true,
  onSet,
}: ToggleRowProps) {
  if (appearance === "plain") {
    const plainBtn =
      "shrink-0 text-ui-text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none dark:text-white"
    return (
      <div className="flex shrink-0 items-center gap-0.5">
        <button
          type="button"
          disabled={disabled}
          title="Отличный вариант"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onSet(value === "star" ? null : "star")
          }}
          className={cn(
            plainBtn,
            value === "star" ? "text-ui-status-review" : "hover:text-amber-200/90 dark:hover:text-amber-300",
          )}
        >
          <Star
            className={cn("h-3 w-3", value === "star" && "fill-amber-400 text-ui-status-review")}
            strokeWidth={1.75}
          />
        </button>
        {includeHidden ? (
          <button
            type="button"
            disabled={disabled}
            title="Не подходит"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onSet(value === "hidden" ? null : "hidden")
            }}
            className={cn(plainBtn, value === "hidden" ? "text-ui-text-secondary" : "hover:text-ui-text-secondary")}
          >
            <EyeOff className="h-3 w-3" strokeWidth={1.75} />
          </button>
        ) : null}
      </div>
    )
  }

  const btn =
    "inline-flex items-center justify-center rounded-lg border shadow-lg transition-colors disabled:opacity-40 disabled:pointer-events-none"
  const sz = dense ? "h-7 w-7" : "h-8 w-8"
  /** Неактивное состояние — как кнопки оверлея студии (таймлайн / версии). */
  const idle = "border-ui-border-strong bg-[var(--ui-chip-bg)] text-ui-text-primary"
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <button
        type="button"
        disabled={disabled}
        title="Отличный вариант"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onSet(value === "star" ? null : "star")
        }}
        className={cn(
          btn,
          sz,
          value === "star"
            ? "border-amber-500/70 bg-amber-950/60 text-ui-status-review"
            : cn(idle, "hover:border-amber-500/40 hover:text-amber-200/90"),
        )}
      >
        <Star className="h-3.5 w-3.5" strokeWidth={1.75} />
      </button>
      {includeHidden ? (
        <button
          type="button"
          disabled={disabled}
          title="Не подходит"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onSet(value === "hidden" ? null : "hidden")
          }}
          className={cn(
            btn,
            sz,
            value === "hidden"
              ? "border-zinc-500/80 bg-ui-bg-elevated/80 text-ui-text-primary"
              : cn(idle, "hover:border-white/25 hover:text-ui-text-primary"),
          )}
        >
          <EyeOff className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
      ) : null}
    </div>
  )
}
